// Central Occasion Registry — the SINGLE data-driven source of truth for the
// Smart Occasion Engine. Adding/editing an occasion = edit data here only.
// Supports Gregorian + Hijri calendars, UAE Official / Admin overrides (applied
// at runtime from backend state), priority, level, per-occasion experience id.
//
// calendar: "gregorian" | "hijri"
// rule (gregorian): { month, day }  |  rule (hijri): { hmonth, hday }
// durationDays: days the experience stays active from the start day
// level: 1 elegant · 2 celebration · 3 grand · 4 ultra/cinematic
// experience: id of the visual experience component (see ./experiences)
// priority: higher wins when two occasions overlap on the same UAE day
// respectful: true => restrained/solemn treatment (no celebration)
// Hijri date resolution precedence (deterministic, never AI):
//   1) UAE Official Override  2) Admin Emergency Override  3) calculated fallback
// Dates verified against official UAE sources (u.ae, mohap.gov.ae, ecd.gov.ae, wam.ae).

export const OCCASIONS = [
  // ===================== UAE NATIONAL (grand / cinematic) =====================
  {
    id: "uae_union_day", name_ar: "اليوم الوطني لدولة الإمارات", name_en: "UAE Union Day",
    category: "uae_national", classification: "national", calendar: "gregorian",
    rule: { month: 12, day: 2 }, durationDays: 3, priority: 100, level: 4,
    experience: "unionDay", cinematicIntro: true,
    greeting_ar: "كل عام والإمارات بخير 🇦🇪", greeting_en: "Happy UAE Union Day",
    active: true, ai: { relevance: "core", tone: "national-celebration" },
  },
  {
    id: "uae_flag_day", name_ar: "يوم العلم", name_en: "UAE Flag Day",
    category: "uae_national", classification: "national", calendar: "gregorian",
    rule: { month: 11, day: 3 }, durationDays: 1, priority: 78, level: 3,
    experience: "flagDay", cinematicIntro: false,
    greeting_ar: "يوم العلم — نرفع رايتنا عالياً", greeting_en: "UAE Flag Day",
    active: true, ai: { relevance: "core", tone: "national-celebration" },
  },
  {
    id: "uae_commemoration_day", name_ar: "يوم الشهيد", name_en: "Commemoration Day",
    category: "uae_national", classification: "commemoration", calendar: "gregorian",
    rule: { month: 11, day: 30 }, durationDays: 1, priority: 88, level: 3,
    experience: "commemoration", respectful: true, cinematicIntro: false,
    greeting_ar: "يوم الشهيد — نستذكر تضحياتهم بكل فخر", greeting_en: "Commemoration Day",
    active: true, ai: { relevance: "core", tone: "respectful-solemn" },
  },
  {
    // Union Pledge Day — officially designated annual national occasion marking the
    // 18 July 1971 signing of the Declaration of the Union & UAE Constitution.
    id: "uae_union_pledge_day", name_ar: "يوم عهد الاتحاد", name_en: "Union Pledge Day",
    category: "uae_national", classification: "national", calendar: "gregorian",
    rule: { month: 7, day: 18 }, durationDays: 1, priority: 98, level: 3,
    experience: "unionPledge", cinematicIntro: false,
    greeting_ar: "يوم عهد الاتحاد — ذكرى توقيع إعلان قيام الاتحاد", greeting_en: "Union Pledge Day",
    active: true, ai: { relevance: "core", tone: "national-celebration" },
  },
  {
    id: "emirati_womens_day", name_ar: "يوم المرأة الإماراتية", name_en: "Emirati Women's Day",
    category: "uae_national", classification: "cultural", calendar: "gregorian",
    rule: { month: 8, day: 28 }, durationDays: 1, priority: 60, level: 2,
    experience: "emiratiWomen",
    greeting_ar: "يوم المرأة الإماراتية", greeting_en: "Emirati Women's Day",
    active: true, ai: { relevance: "core", tone: "elegant-neutral" },
  },
  {
    id: "emirati_children_day", name_ar: "يوم الطفل الإماراتي", name_en: "Emirati Children's Day",
    category: "uae_national", classification: "cultural", calendar: "gregorian",
    rule: { month: 3, day: 15 }, durationDays: 1, priority: 50, level: 2,
    experience: "cultural",
    greeting_ar: "يوم الطفل الإماراتي", greeting_en: "Emirati Children's Day",
    active: true, ai: { relevance: "supporting", tone: "joyful-celebration" },
  },
  {
    id: "emirati_elderly_day", name_ar: "يوم المسن الإماراتي", name_en: "Emirati Elderly Day",
    category: "uae_national", classification: "cultural", calendar: "gregorian",
    rule: { month: 10, day: 1 }, durationDays: 1, priority: 46, level: 1,
    experience: "nationalElegant",
    greeting_ar: "يوم المسن الإماراتي", greeting_en: "Emirati Elderly Day",
    active: true, ai: { relevance: "supporting", tone: "respectful-solemn" },
  },

  // ===================== ISLAMIC (Hijri) =====================
  {
    // Zayed Humanitarian Work Day — observed on 19 Ramadan (marks Sheikh Zayed's
    // passing). Surfaces a respectful humanitarian theme for that one day, above
    // the general Ramadan ambience (priority > Ramadan) — a deliberate conflict rule.
    id: "zayed_humanitarian_day", name_ar: "يوم زايد للعمل الإنساني", name_en: "Zayed Humanitarian Work Day",
    category: "uae_national", classification: "commemoration", calendar: "hijri",
    rule: { hmonth: 9, hday: 19 }, durationDays: 1, priority: 92, level: 3,
    experience: "zayedHumanitarian", respectful: true,
    greeting_ar: "يوم زايد للعمل الإنساني", greeting_en: "Zayed Humanitarian Work Day",
    active: true, ai: { relevance: "core", tone: "respectful-solemn" },
  },
  {
    id: "ramadan", name_ar: "شهر رمضان المبارك", name_en: "Ramadan",
    category: "islamic", classification: "religious", calendar: "hijri",
    rule: { hmonth: 9, hday: 1 }, durationDays: 30, priority: 90, level: 3,
    experience: "ramadan", cinematicIntro: true,
    greeting_ar: "رمضان كريم", greeting_en: "Ramadan Kareem",
    active: true, ai: { relevance: "core", tone: "spiritual-calm" },
  },
  {
    id: "laylat_al_qadr", name_ar: "ليلة القدر", name_en: "Laylat al-Qadr",
    category: "islamic", classification: "religious", calendar: "hijri",
    rule: { hmonth: 9, hday: 27 }, durationDays: 1, priority: 94, level: 4,
    experience: "laylatAlQadr", cinematicIntro: true,
    greeting_ar: "ليلة القدر خير من ألف شهر", greeting_en: "Laylat al-Qadr",
    active: true, ai: { relevance: "core", tone: "spiritual-calm" },
  },
  {
    id: "eid_al_fitr", name_ar: "عيد الفطر المبارك", name_en: "Eid Al Fitr",
    category: "islamic", classification: "religious", calendar: "hijri",
    rule: { hmonth: 10, hday: 1 }, durationDays: 3, priority: 96, level: 4,
    experience: "eidAlFitr", cinematicIntro: true,
    greeting_ar: "عيد فطر مبارك", greeting_en: "Eid Mubarak",
    active: true, ai: { relevance: "core", tone: "joyful-celebration" },
  },
  {
    id: "arafah_day", name_ar: "يوم عرفة", name_en: "Day of Arafah",
    category: "islamic", classification: "religious", calendar: "hijri",
    rule: { hmonth: 12, hday: 9 }, durationDays: 1, priority: 93, level: 3,
    experience: "arafah", cinematicIntro: false,
    greeting_ar: "تقبّل الله صالح الأعمال — يوم عرفة", greeting_en: "Day of Arafah",
    active: true, ai: { relevance: "core", tone: "spiritual-calm" },
  },
  {
    id: "eid_al_adha", name_ar: "عيد الأضحى المبارك", name_en: "Eid Al Adha",
    category: "islamic", classification: "religious", calendar: "hijri",
    rule: { hmonth: 12, hday: 10 }, durationDays: 4, priority: 96, level: 4,
    experience: "eidAlAdha", cinematicIntro: true,
    greeting_ar: "عيد أضحى مبارك", greeting_en: "Eid Al Adha Mubarak",
    active: true, ai: { relevance: "core", tone: "joyful-celebration" },
  },
  {
    id: "islamic_new_year", name_ar: "رأس السنة الهجرية", name_en: "Islamic New Year",
    category: "islamic", classification: "religious", calendar: "hijri",
    rule: { hmonth: 1, hday: 1 }, durationDays: 1, priority: 62, level: 2,
    experience: "hijriNewYear",
    greeting_ar: "كل عام وأنتم بخير — عام هجري جديد", greeting_en: "Happy Islamic New Year",
    active: true, ai: { relevance: "core", tone: "spiritual-calm" },
  },
  {
    id: "prophet_birthday", name_ar: "المولد النبوي الشريف", name_en: "Mawlid al-Nabi",
    category: "islamic", classification: "religious", calendar: "hijri",
    rule: { hmonth: 3, hday: 12 }, durationDays: 1, priority: 66, level: 3,
    experience: "mawlid", cinematicIntro: false,
    greeting_ar: "المولد النبوي الشريف", greeting_en: "Mawlid al-Nabi",
    active: true, ai: { relevance: "core", tone: "spiritual-calm" },
  },
  {
    id: "isra_miraj", name_ar: "الإسراء والمعراج", name_en: "Isra and Mi'raj",
    category: "islamic", classification: "religious", calendar: "hijri",
    rule: { hmonth: 7, hday: 27 }, durationDays: 1, priority: 58, level: 2,
    experience: "islamicElegant",
    greeting_ar: "ذكرى الإسراء والمعراج", greeting_en: "Isra and Mi'raj",
    active: true, ai: { relevance: "supporting", tone: "spiritual-calm" },
  },

  // ===================== INTERNATIONAL / PROFESSIONAL =====================
  {
    id: "gregorian_new_year", name_ar: "رأس السنة الميلادية", name_en: "New Year",
    category: "international", classification: "international", calendar: "gregorian",
    rule: { month: 1, day: 1 }, durationDays: 2, priority: 45, level: 3,
    experience: "newYear", cinematicIntro: true,
    greeting_ar: "سنة جديدة سعيدة", greeting_en: "Happy New Year",
    active: true, ai: { relevance: "supporting", tone: "joyful-celebration" },
  },
  {
    id: "intl_accounting_day", name_ar: "اليوم العالمي للمحاسبة", name_en: "International Accounting Day",
    category: "corporate", classification: "international", calendar: "gregorian",
    rule: { month: 11, day: 10 }, durationDays: 1, priority: 48, level: 2,
    experience: "corporate",
    greeting_ar: "اليوم العالمي للمحاسبة", greeting_en: "International Accounting Day",
    active: true, ai: { relevance: "core", tone: "elegant-neutral" },
  },
];

// ---- Extended observances (data-driven, proves 50+ scale, zero code changes) ----
// [id, ar, en, month, day, experience, active]
const EXTRA = [
  ["intl_womens_day", "اليوم العالمي للمرأة", "International Women's Day", 3, 8, "international", true],
  ["labour_day", "عيد العمال", "Labour Day", 5, 1, "international", true],
  ["intl_smes_day", "اليوم العالمي للمشاريع الصغيرة والمتوسطة", "MSMEs Day", 6, 27, "corporate", true],
  ["intl_savings_day", "اليوم العالمي للادخار", "World Savings Day", 10, 31, "corporate", true],
  ["intl_data_privacy_day", "اليوم العالمي لخصوصية البيانات", "Data Privacy Day", 1, 28, "corporate", true],
  ["intl_arabic_language_day", "اليوم العالمي للغة العربية", "World Arabic Language Day", 12, 18, "cultural", true],
  ["intl_tolerance_day", "اليوم العالمي للتسامح", "Intl. Day of Tolerance", 11, 16, "cultural", true],
  ["intl_charity_day", "اليوم العالمي للأعمال الخيرية", "Intl. Day of Charity", 9, 5, "cultural", true],
  ["intl_youth_day", "اليوم العالمي للشباب", "Intl. Youth Day", 8, 12, "international", false],
  ["intl_literacy_day", "اليوم العالمي لمحو الأمية", "Intl. Literacy Day", 9, 8, "international", false],
  ["intl_peace_day", "اليوم العالمي للسلام", "Intl. Day of Peace", 9, 21, "international", false],
  ["intl_tourism_day", "اليوم العالمي للسياحة", "World Tourism Day", 9, 27, "international", false],
  ["intl_teachers_day", "اليوم العالمي للمعلم", "World Teachers' Day", 10, 5, "international", false],
  ["intl_food_day", "يوم الأغذية العالمي", "World Food Day", 10, 16, "international", false],
  ["intl_science_day", "اليوم العالمي للعلوم", "World Science Day", 11, 10, "international", false],
  ["intl_students_day", "اليوم العالمي للطلاب", "Intl. Students' Day", 11, 17, "international", false],
  ["intl_volunteer_day", "اليوم العالمي للتطوع", "Intl. Volunteer Day", 12, 5, "cultural", false],
  ["intl_human_rights_day", "اليوم العالمي لحقوق الإنسان", "Human Rights Day", 12, 10, "international", false],
  ["intl_migrants_day", "اليوم العالمي للمهاجرين", "Intl. Migrants Day", 12, 18, "international", false],
  ["intl_zero_discrimination", "يوم عدم التمييز", "Zero Discrimination Day", 3, 1, "cultural", false],
  ["intl_happiness_day", "اليوم العالمي للسعادة", "Intl. Day of Happiness", 3, 20, "cultural", false],
  ["intl_water_day", "اليوم العالمي للمياه", "World Water Day", 3, 22, "international", false],
  ["earth_day", "يوم الأرض", "Earth Day", 4, 22, "international", false],
  ["intl_health_day", "يوم الصحة العالمي", "World Health Day", 4, 7, "international", false],
  ["intl_creativity_day", "اليوم العالمي للإبداع", "World Creativity Day", 4, 21, "cultural", false],
  ["intl_book_day", "اليوم العالمي للكتاب", "World Book Day", 4, 23, "cultural", false],
  ["intl_family_day", "اليوم العالمي للأسرة", "Intl. Day of Families", 5, 15, "cultural", false],
  ["intl_biodiversity_day", "اليوم العالمي للتنوع البيولوجي", "Biodiversity Day", 5, 22, "international", false],
  ["intl_environment_day", "اليوم العالمي للبيئة", "World Environment Day", 6, 5, "international", false],
  ["intl_population_day", "اليوم العالمي للسكان", "World Population Day", 7, 11, "international", false],
  ["intl_humanitarian_day", "اليوم العالمي للعمل الإنساني", "World Humanitarian Day", 8, 19, "cultural", false],
  ["intl_older_persons_day", "اليوم العالمي لكبار السن", "Intl. Day of Older Persons", 10, 1, "cultural", false],
];
for (const [id, ar, en, month, day, experience, active] of EXTRA) {
  OCCASIONS.push({
    id, name_ar: ar, name_en: en,
    category: experience === "corporate" ? "corporate" : experience === "cultural" ? "cultural" : "international",
    classification: "international", calendar: "gregorian", rule: { month, day }, durationDays: 1,
    priority: experience === "corporate" ? 34 : 22, level: 1, experience,
    greeting_ar: ar, greeting_en: en, active: !!active,
    ai: { relevance: active ? "supporting" : "optional", tone: "elegant-neutral" },
  });
}

export const OCCASION_COUNT = OCCASIONS.length;
export const ACTIVE_OCCASIONS = OCCASIONS.filter((o) => o.active);
